'use client';

import React from 'react';
import Link from 'next/link';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { PulseButton, EmptyState } from 'src/components/PulseUI';
import { useGetPlaysQuery, useGetPlaysDetailsQuery } from 'src/api/redux/';
import { useAppDispatch } from 'src/redux/hooks';
import { setSelectedPlay } from 'src/redux/slices/strategyFormSlice';
import { clearDynamicCriteria } from 'src/redux/slices/aiListGeneratorSlice';
import { LOCATION } from 'src/hooks/filters/constants';
import { AsideItem } from '../AsideItem';

interface ChoosePlayStepProps {
  onContinue: () => void;
}

const ChoosePlayStep: React.FC<ChoosePlayStepProps> = ({ onContinue }) => {
  const dispatch = useAppDispatch();
  const [selectedPlayId, setSelectedPlayId] = React.useState<string | null>(
    null
  );

  const { data: plays } = useGetPlaysQuery();
  const { data: playDetails } = useGetPlaysDetailsQuery(
    { playId: selectedPlayId },
    {
      skip: !selectedPlayId,
    }
  );

  // Update Redux when playDetails changes
  React.useEffect(() => {
    if (playDetails) {
      dispatch(setSelectedPlay(playDetails as import('src/redux/slices/strategyFormSlice').SelectedPlay));
    }
  }, [playDetails, dispatch]);

  const handleSelectPlay = (playId: string) => {
    // Clear existing criteria when selecting a different play
    // This ensures the new play's template will be hydrated fresh
    if (playId !== selectedPlayId) {
      dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
      dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY_ENGAGE }));
    }
    setSelectedPlayId(playId);
  };

  const hasPlayDetails = !!playDetails;

  return (
    <div className="flex h-[calc(100vh-22rem)] flex-col gap-4 lg:flex-row lg:gap-6">
      {/* Sidebar - Top on mobile, Left on desktop */}
      <aside
        className="w-full shrink-0 overflow-hidden lg:h-full lg:w-72"
        aria-label="Template selection"
        data-testid="configure-sidebar"
      >
        <div className="flex flex-col gap-4 overflow-y-auto rounded-lg border border-neutral-200 bg-white p-4 lg:h-full">
          {/* Build Custom Strategy */}
          <Link
            href="/settings/admin/strategy-manager/"
            aria-label="Build a custom strategy"
            data-testid="build-custom-strategy-link"
          >
            <span className="text-xs text-link hover:underline">
              Build Custom Strategy <Icon icon={faArrowRight} size="xs" />
            </span>
          </Link>
          <div className="h-px w-full shrink-0 bg-neutral-200" />
          {/* Template List - Vertical on desktop, Horizontal scroll on mobile */}
          <div className="flex flex-row gap-3 overflow-x-auto  pb-2 lg:flex-col lg:overflow-x-visible lg:pb-0">
            {(plays?.results || []).map((play, index) => (
              <AsideItem
                key={`${play.id}-${index}`}
                item={play}
                isSelected={selectedPlayId === play.id}
                onClick={() => handleSelectPlay(play.id)}
              />
            ))}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className="min-w-0 flex-1"
        aria-label="Play details"
        data-testid="configure-main"
      >
        <div className="flex h-full flex-col gap-6 overflow-y-auto rounded-lg border border-neutral-200 bg-white p-6">
          {hasPlayDetails && (
            <>
              {/* Content Header */}
              <div className="flex items-start">
                <div className="flex flex-col gap-3">
                  <h2
                    className="text-xl font-semibold text-neutral-900"
                    data-testid="play-title"
                  >
                    {playDetails?.name || 'Select a Play'}
                  </h2>
                  {/* Tags */}
                  {playDetails?.tags?.length > 0 && (
                    <div
                      className="flex flex-wrap gap-2"
                      aria-label="Play categories"
                      data-testid="play-tags"
                    >
                      {playDetails.tags.map((tag: string, index: number) => (
                        <span
                          key={index}
                          className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs text-neutral-600"
                          data-testid={`play-tag-${index}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {playDetails?.description && (
                    <p
                      className="text-sm text-neutral-500"
                      data-testid="play-description"
                    >
                      {playDetails?.description}
                    </p>
                  )}
                </div>
                {/* Action Buttons */}
                <PulseButton
                  variant="solid"
                  color="primary"
                  disabled={!hasPlayDetails}
                  onClick={onContinue}
                  data-testid="continue-to-configure-button"
                  aria-label="Configure this play"
                  endDecorator={faArrowRight}
                  className="flex-shrink-0 whitespace-nowrap"
                >
                  Configure This Play
                </PulseButton>
              </div>

              {/* What It Does Section */}
              <section
                className="flex flex-col gap-3"
                aria-labelledby="what-it-does-heading"
                data-testid="what-it-does-section"
              >
                <h3
                  id="what-it-does-heading"
                  className="text-xs font-semibold uppercase tracking-wide text-tertiary"
                >
                  What It Does
                </h3>
                {playDetails?.target_template && (
                  <div
                    className="flex flex-col gap-1"
                    data-testid="target-template-info"
                  >
                    <p className="text-sm font-medium text-primary">
                      {playDetails.target_template.name}
                    </p>
                    <p className="text-sm text-secondary">
                      {playDetails.target_template.description}
                    </p>
                  </div>
                )}
                {playDetails?.engage_template && (
                  <div
                    className="flex flex-col gap-1"
                    data-testid="engage-template-info"
                  >
                    <p className="text-sm font-medium text-primary">
                      {playDetails.engage_template.name}
                    </p>
                    <p className="text-sm text-secondary">
                      {playDetails.engage_template.description}
                    </p>
                  </div>
                )}
              </section>
            </>
          )}

          {!hasPlayDetails && (
            <div
              className="flex flex-1 items-center justify-center"
              data-testid="empty-state-wrapper"
            >
              <EmptyState
                icon={
                  <Icon
                    icon={faPlay}
                    className="text-4xl text-neutral-400/80"
                    aria-hidden="true"
                  />
                }
                title="Select a Play"
                description="Choose a play from the sidebar to view details and configure your strategy."
                size="md"
                className="border-0 bg-transparent shadow-none"
              />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChoosePlayStep;
